<template>
  <TransitionFromTop>
    <div :class="[style.thankYouPageAnswer, mobileVersion ? style.mobile : '']">
      <video
        v-if="isCurrentMediaVideo"
        :src="getImg()"
        autoplay
        muted
        loop
        playsinline
        webkit-playsinline
        preload="auto"
      />

      <img v-else :src="getImg()" alt="" />

      <label for="image"></label>

      <input
        type="file"
        accept="image/*, video/*"
        id="image"
        @change="
          setImg($event, selectedQuestion + '-' + currentQuestion.type + '.')
        "
      />

      <button-cross :class="style.crossButton" @click="delImg" />
    </div>
  </TransitionFromTop>
</template>

<script lang="ts">
import {defineComponent} from "vue";
import ButtonCross from "@/elements/buttonCross/buttonCross.vue";
import style from "./ThankYouPageAnswer.module.scss";
import {DOMAIN} from "@/http";
import TransitionFromTop from "@/dynamics/transitions/TransitionFromTop/TransitionFromTop.vue";
import {questions} from "@/page/quiz/typeQuiz";

export default defineComponent({
  name: "ThankYouPageAnswer",

  components: {
    TransitionFromTop,
    ButtonCross
  },

  data() {
    return {
      style,
      DOMAIN,
      localPreviewUrl: "",
      cachedPreviewUrls: {} as Record<string, string>,
      localMediaType: "" as "image" | "video" | ""
    };
  },

  methods: {
    getImg(): string {
      if (this.localPreviewUrl) {
        return this.localPreviewUrl;
      }

      const path = this.currentQuestion?.thankYouImg;

      if (!path) {
        return `https://${this.DOMAIN}/api/img?name=logoWidget.svg`;
      }

      if (path.startsWith("blob:") || path.startsWith("data:")) {
        return path;
      }

      const cachedPreviewUrl = this.getCachedPreviewUrl(path);

      if (cachedPreviewUrl) {
        return cachedPreviewUrl;
      }

      return `https://${this.DOMAIN}/api/img?name=${path}`;
    },

    delImg(): void {
      if (!this.currentQuestion) {
        return;
      }

      this.revokeLocalPreview();
      this.removeOldThankYouImageFromCachedFiles();

      this.currentQuestion.thankYouImg = "";
      this.localMediaType = "";
    },

    async setImg(event: Event, name: string): Promise<void> {
      const target = event.target as HTMLInputElement;

      if (!target.files || target.files.length === 0) {
        return;
      }

      const file = target.files[0];
      const blob = file.slice(0, file.size, file.type);
      const nameSplit = file.name.split(".");
      const type = nameSplit[nameSplit.length - 1];
      const newName = `${name}${type}`;
      const newFile = new File([blob], newName, {
        type: file.type
      });

      this.revokeLocalPreview();

      const srcFile = URL.createObjectURL(newFile);
      this.localPreviewUrl = srcFile;

      this.removeOldThankYouImageFromCachedFiles(newFile.name);

      this.cachedFiles.push(newFile);

      this.currentQuestion.thankYouImg = newFile.name;
      this.localMediaType = this.isVideoFile(file) ? "video" : "image";

      target.value = "";
    },

    isVideoFile(file: File): boolean {
      return file.type.startsWith("video/") || this.isVideoPath(file.name);
    },

    isVideoPath(path = ""): boolean {
      return /\.(mp4|webm|ogg)(?:$|[?#])/i.test(path);
    },

    removeOldThankYouImageFromCachedFiles(nextFileName?: string): void {
      const currentType = this.currentQuestion?.type ?? "thankYouPage";
      const imagePrefix = `${this.selectedQuestion}-${currentType}.`;

      for (let index = this.cachedFiles.length - 1; index >= 0; index -= 1) {
        const cachedFile = this.cachedFiles[index];

        if (!cachedFile.name.startsWith(imagePrefix)) {
          continue;
        }

        if (nextFileName && cachedFile.name === nextFileName) {
          this.cachedFiles.splice(index, 1);
          continue;
        }

        if (!nextFileName) {
          this.cachedFiles.splice(index, 1);
        }
      }
    },

    revokeLocalPreview(): void {
      if (this.localPreviewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(this.localPreviewUrl);
      }

      this.localPreviewUrl = "";
    },

    getCachedPreviewUrl(fileName: string): string {
      if (!fileName) {
        return "";
      }

      const currentPreviewUrl = this.cachedPreviewUrls[fileName];

      if (currentPreviewUrl) {
        return currentPreviewUrl;
      }

      const cachedFile = this.cachedFiles.find((file) => {
        return file.name === fileName;
      });

      if (!cachedFile) {
        return "";
      }

      const previewUrl = URL.createObjectURL(cachedFile);

      this.cachedPreviewUrls = {
        ...this.cachedPreviewUrls,
        [fileName]: previewUrl
      };

      return previewUrl;
    },

    revokeCachedPreviews(): void {
      Object.values(this.cachedPreviewUrls).forEach((url) => {
        if (url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });

      this.cachedPreviewUrls = {};
    }
  },

  computed: {
    quiz() {
      return this.$quizStore.getQuiz;
    },

    selectedQuestion(): number {
      return this.$quizStore.getSelectedQuestion;
    },

    selectedVersion(): string {
      return this.$quizStore.getSelectedVersion;
    },

    mobileVersion(): boolean {
      return this.selectedVersion === "Мобильная";
    },

    cachedFiles(): File[] {
      return this.$quizStore.getCachedFiles;
    },

    currentQuestion(): questions {
      return this.quiz.questions[this.selectedQuestion];
    },

    isCurrentMediaVideo(): boolean {
      if (this.localMediaType) {
        return this.localMediaType === "video";
      }

      return this.isVideoPath(this.currentQuestion?.thankYouImg || "");
    }
  },

  beforeUnmount() {
    this.revokeLocalPreview();
    this.revokeCachedPreviews();
  },

  watch: {
    cachedFiles(): void {
      this.revokeCachedPreviews();
    },

    selectedQuestion(): void {
      this.revokeLocalPreview();
      this.revokeCachedPreviews();
      this.localMediaType = "";
    }
  }
});
</script>
